import {match} from 'ts-pattern';
import {DeepPartial} from 'utility-types';

import {clone, isEmpty} from 'ramda';

import {
  ChangedFieldValue,
  DispensingPrices,
  PricesSettings,
  PutArticleCalculationResponse,
  usePutArticleCalculationMutation,
} from '@dms/api/warehouse';
import {useFormRecalculation} from '@dms/warehouse/shared-components';

import {Nullish, useOnMount} from 'shared/web';

import {ArticleDetailForm} from '../types/ArticleDetailFormSchema';

interface UseArticleFormRecalculationConfig {
  articleId: string;
  warehouseId: string;
  defaultValues: DeepPartial<ArticleDetailForm>;
}

/**
 * Custom hook for handling article form recalculation with proper type handling
 * for the article calculation mutation. This is a specialized wrapper around the
 * generic useFormRecalculation hook.
 */
export function useArticleFormRecalculation(config: UseArticleFormRecalculationConfig) {
  const [articleCalculate, {isLoading: isArticleCalculateLoading}] =
    usePutArticleCalculationMutation();

  const formRecalculation = useFormRecalculation<
    ArticleDetailForm,
    {
      changedFieldValue: {
        field: ChangedFieldValue['field'];
        value: string;
      };
      currentFeFieldsValues: {
        supplierId: string | Nullish;
        manufacturerId: string | Nullish;
        manufacturerNumber: string | Nullish;
        vatType: string | Nullish;
        discountGroup: string | Nullish;
        marketingCode: string | Nullish;
        pricesSettings: PricesSettings;
        dispensingPrices: DispensingPrices;
      };
    },
    PutArticleCalculationResponse
  >({
    allowedFields: ALLOWED_VALUES,
    allowedObjects: ALLOWED_VALUES_IN_OBJECT,
    recalculateMutation: (params) => ({
      unwrap: () =>
        articleCalculate({
          warehouseId: config.warehouseId,
          articleId: config.articleId,
          body: params.body,
          signal: params.signal,
        }).unwrap(),
    }),
    requestConfig: {
      articleId: config.articleId,
      warehouseId: config.warehouseId,
    },
    buildRequestBody: (values, changedField, changedValue) => ({
      changedFieldValue: {
        field: match(changedField)
          .returnType<ChangedFieldValue['field']>()
          .with('basePriceWithoutVat', () => 'purchasePrice')
          .with('recommendedPurchasePriceWithoutVat', () => 'recommendedPurchasePriceWithOutVat')
          .with('purchasePriceDefSupplierWithoutVat', () => 'purchasePriceDefSupplierWithoutVat')
          .otherwise(() => changedField as ChangedFieldValue['field']),
        value: String(
          changedValue === null && ALLOWED_VALUES_WITHOUT_NULL.includes(changedField)
            ? 0
            : changedValue
        ),
      },
      currentFeFieldsValues: {
        supplierId: values.generalInformation?.basicInformation?.supplierId,
        manufacturerId: values.generalInformation?.basicInformation?.manufacturerId,
        manufacturerNumber: values.generalInformation?.basicInformation?.manufacturerNumber,
        vatType: values.generalInformation?.basicInformation?.vatType,
        discountGroup: values.generalInformation?.basicInformation?.supplierDiscountGroupId,
        marketingCode: values.generalInformation?.basicInformation?.marketingCode,
        pricesSettings: values.prices?.pricesSettings as PricesSettings,
        dispensingPrices: values.prices?.dispensingPrices as DispensingPrices,
      },
    }),
    transformResponse: (response, values) =>
      clone({
        ...values,
        prices: {
          ...values.prices,
          pricesSettings: response.pricesSettings,
          dispensingPrices: response.dispensingPrices,
        },
      }),
    isDifferenceEmpty: (allowedDifference) => {
      const pricesDiff = allowedDifference?.prices;
      const isDispensingPricesDiffEmpty = pricesDiff?.dispensingPrices
        ? isEmpty(pricesDiff.dispensingPrices)
        : false;
      const isPricesSettingsDiffEmpty = pricesDiff?.pricesSettings
        ? isEmpty(pricesDiff.pricesSettings)
        : false;

      return isDispensingPricesDiffEmpty && isPricesSettingsDiffEmpty;
    },
    mapFieldName: (changedField) => {
      if (changedField === 'salesPriceCalculation' || changedField === 'basePriceSource') {
        return 'purchasePrice';
      }
      return changedField;
    },
  });

  useOnMount(() => {
    formRecalculation.setPrevFormValues(config.defaultValues);
  });

  return {
    ...formRecalculation,
    isArticleCalculateLoading,
  };
}

const ALLOWED_VALUES_IN_OBJECT = [
  'generalInformation',
  'basicInformation',
  'prices',
  'pricesSettings',
  'dispensingPrices',
];

// These values are allowed to be null, because BE does accept null values for these fields
const ALLOWED_VALUES_WITH_NULL = ['vatType', 'supplierId', 'marketingCode'];

// These values are not allowed to be null, because BE does not accept null values for these fields
const ALLOWED_VALUES_WITHOUT_NULL = [
  'purchasePrice',
  'recommendedPriceWithOutVat',
  'recommendedPriceWithVat',
  'recommendedPurchasePriceWithoutVat',
  'purchasePriceDefSupplierWithoutVat',
  'saleBasePriceWithoutVat',
  'saleBasePriceWithVat',
  'saleBaseMarkUpPercent',
  'saleBaseProfit',
  'saleBaseMarginPercent',
  'warrantyPriceWithoutVat',
  'warrantyPriceWithVat',
  'warrantyMarkUpPercent',
  'warrantyProfit',
  'warrantyMarginPercent',
  'basePriceWithoutVat',
  'salesPriceCalculation',
  'basePriceSource',
  'unitSalesPriceWithVat',
];

const ALLOWED_VALUES = [...ALLOWED_VALUES_WITH_NULL, ...ALLOWED_VALUES_WITHOUT_NULL];
